// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AssetPageHeader from './AssetPageHeader'
import AssetPageGraph from './AssetPageGraph'
import AssetPageGraphControls from './AssetPageGraphControls'
import NewNavbar from '../NewAssetDashboard/NewNavbar'
import { Skeleton, SkeletonChart } from '../../components/Skeleton'
import { fetchAssetGroups } from '../../store/assetGroupSlice'
import { fetchAssetsByGroupId } from '../../store/assetsSlice'
// The AssetPage component is the main component for displaying detailed information about a specific asset. It manages the state and data fetching for the selected asset, and composes the header, graph, and top bar components to create a cohesive user interface for viewing asset details and interacting with the asset's data.
const AssetPage = () => {
  const dispatch = useDispatch()
  const { groupid, assetid } = useParams()
  const assets = useSelector((state: any) => state.assets)
  const assetGroups = useSelector((state: any) => state.assetGroups)
  const user = useSelector((state: any) => state.user)
  const [forecastControls, setForecastControls] = useState(null)
  const [graphControls, setGraphControls] = useState(null)
  const [viewControls, setViewControls] = useState(null)
  const downloadHandlerRef = useRef(null)
  const registerDownload = useCallback((handler) => {
    downloadHandlerRef.current = handler || null
  }, [])

  const primaryGroupId = useMemo(() => {
    const groupIds = Object.keys(assetGroups?.groups || {})
    return groupIds.length > 0 ? groupIds[0] : null
  }, [assetGroups?.groups])

  const activeGroupId = useMemo(() => {
    return groupid || primaryGroupId
  }, [groupid, primaryGroupId])

  useEffect(() => {
    if (!primaryGroupId) {
      dispatch(fetchAssetGroups())
    }
  }, [dispatch, primaryGroupId])

  useEffect(() => {
    if (activeGroupId && !assets?.dataGroups?.[activeGroupId]) {
      dispatch(fetchAssetsByGroupId(activeGroupId))
    }
  }, [dispatch, activeGroupId, assets?.dataGroups])

  const selectedAsset = useMemo(() => {
    if (!activeGroupId || !assetid) return null
    const assetList = assets?.dataGroups?.[activeGroupId]
    if (!assetList || assetList.length === 0) return null
    return assetList.find((asset) => String(asset?.id) === String(assetid)) || null
  }, [assets?.dataGroups, activeGroupId, assetid])

  const selectedGroup = useMemo(() => {
    if (!activeGroupId) return null
    return assetGroups?.groups?.[activeGroupId] || null
  }, [assetGroups?.groups, activeGroupId])

  const organization = useMemo(() => {
    return user?.organizations?.data?.[selectedGroup?.organizationId] || null
  }, [selectedGroup?.organizationId, user?.organizations?.data])

  const ownerOrOrgLabel = useMemo(() => {
    if (organization?.name) {
      return organization.name
    }

    const isCurrentUserOwner = selectedGroup?.owner && selectedGroup.owner === user?.permissionDetails?.id
    if (isCurrentUserOwner) {
      return user?.permissionDetails?.name || user?.details?.name || 'You'
    }

    return selectedGroup?.owner || 'Unknown Owner'
  }, [
    organization?.name,
    selectedGroup?.owner,
    user?.permissionDetails?.id,
    user?.permissionDetails?.name,
    user?.details?.name,
  ])

  // Placeholders in the real layout rather than a spinner, so the page lands in place
  if (!activeGroupId || !assets?.dataGroups?.[activeGroupId]) {
    return (
      <div className="flex-1 flex h-screen max-h-screen max-w-full overflow-hidden">
        <div className="rounded-[8px] p-4 flex-1 flex flex-col max-w-full min-h-0 gap-3">
          <Skeleton className="h-6 w-64 shrink-0" />
          <div className="flex shrink-0 items-center gap-4">
            <Skeleton className="h-16 w-16 shrink-0 rounded-eai-card" radius="none" />
            <div className="min-w-0 flex-1">
              <Skeleton className="h-6 w-1/2 max-w-xs" />
              <Skeleton className="mt-2 h-3 w-1/3 max-w-[16rem]" />
            </div>
          </div>
          <div className="min-h-0 flex-1">
            <SkeletonChart label="Loading forecast" />
          </div>
        </div>
      </div>
    )
  }
  if (!selectedAsset) {
    return (
      <div className="flex flex-col grow bg-white max-h-[100vh] items-center justify-center text-sm text-slate-600">
        Select an asset from the map to view details.
      </div>
    )
  }

  return ( // The main asset page layout.
    <div className="flex-1 flex h-screen max-h-screen max-w-full overflow-hidden">
      <div className="rounded-[8px] p-4 flex-1 flex flex-col max-w-full min-h-0 gap-3">
        <NewNavbar 
          organization={organization}
          groupName={selectedGroup?.name}
          assetName={selectedAsset?.name}
        />
        <AssetPageHeader
          selectedAsset={selectedAsset}
          ownerOrOrgLabel={ownerOrOrgLabel}
        />
        <AssetPageGraphControls
          forecastControls={forecastControls}
          graphControls={graphControls}
          viewControls={viewControls}
          onDownloadReport={() => downloadHandlerRef.current && downloadHandlerRef.current()}
        />
        <AssetPageGraph
          selectedAsset={selectedAsset}
          onRegisterDownload={registerDownload}
          onRegisterForecastControls={setForecastControls}
          onRegisterGraphControls={setGraphControls}
          onRegisterViewControls={setViewControls}
        />
      </div>
    </div>
  )
}

export default AssetPage
